import React from 'react';
import {Chip, IconButton, Stack, Tooltip, Typography} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import {formatValue, getNestedValue} from '../../utils/formatters';
import {getReportStatusChipColor} from '../../utils/reportStatusColor';
import ValidationStatusCell from './ValidationStatusCell';
import TableRowActionsMenu from './TableRowActionsMenu';
import DeleteReportDialog from '../../reportEditorPage/reportHeader/DeleteReportDialog';
import ShareDialog from '../../reportEditorPage/reportHeader/ShareDialog';
import DisclosuresListModal from '../FintelTable/DisclosuresListModal';
import {useTableRowActions} from './useTableRowActions';
import {useNavigate} from 'react-router';
import ProductTitleLink from '../common/ProductTitleLink';
import AuthorChip from '../common/AuthorChip';
import {resolveReportLinkedIcrNumber} from '../../../../firebase/firestore/icr';

function resolveIcrLink(report) {
    const icrNumber = resolveReportLinkedIcrNumber(report);
    const icrSubject = report.icrSubject || null;
    return {icrNumber, icrSubject};
}

/**
 * Single row in the reports table
 * @param {Object} report - Report data
 * @param {Array} columns - Column configurations
 * @param {boolean} enableDisclosureCreation - Whether to enable disclosure creation (for Fintel reports)
 * @param {function} [onRowChanged] - Called after an action mutates the row (delete / reset) so the list can refresh
 */
const TableRow = ({report, columns, enableDisclosureCreation = false, onRowChanged}) => {
    const navigate = useNavigate();
    // Menu state
    const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
    const menuOpen = Boolean(menuAnchorEl);

    // Dialog state
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const [shareOpen, setShareOpen] = React.useState(false);
    const [disclosuresListOpen, setDisclosuresListOpen] = React.useState(false);

    // Actions hook
    const {
        handleDelete,
        handleExport,
        handleCreateDisclosure,
        handleResetToDraft,
        deleting,
        exporting,
        resettingToDraft,
        role,
        isShared,
        canDelete,
        isFintel,
    } = useTableRowActions({
        reportId: report.id,
        title: report.title,
        report,
        enableDisclosureCreation
    });

    const {icrNumber, icrSubject} = resolveIcrLink(report);

    const handleMenuOpen = (e) => {
        e.stopPropagation();
        setMenuAnchorEl(e.currentTarget);
    };

    const handleDeleteConfirm = async () => {
        await handleDelete();
        setConfirmOpen(false);
        onRowChanged?.(report.id);
    };

    const handleResetToDraftClick = async () => {
        if (!handleResetToDraft) return;
        try {
            await handleResetToDraft();
            onRowChanged?.(report.id);
        } catch (err) {
            console.error('Failed to reset report to Draft:', err);
        }
    };

    const handleDisclosureCountClick = (e) => {
        e.stopPropagation();
        setDisclosuresListOpen(true);
    };

    const handleDisclosureClick = (disclosureId) => {
        navigate(`/products/${disclosureId}/edit`);
    };

    const handleCreateDisclosureClick = async () => {
        if (!handleCreateDisclosure) return;
        try {
            const disclosureId = await handleCreateDisclosure();
            if (disclosureId) {
                navigate(`/products/${disclosureId}/edit`);
            }
        } catch (err) {
            console.error('Failed to create disclosure:', err);
        }
    };

    return (
        <>
            <Stack
                direction="row"
                spacing={2}
                px={2}
                py={2}
                alignItems="center"
                bgcolor="white"
                sx={{
                    '&:hover': {
                        backgroundColor: '#f9f9f9',
                    },
                }}
            >
                {columns.map((column, index) => {
                    const value = getNestedValue(report, column.field);

                    // Render Product name as a link
                    if (column.field === 'title') {
                        const sharedByName = isShared ? (report.userDisplayName || 'another user') : null;
                        return (
                            <ProductTitleLink
                                key={index}
                                to={`/products/${report.id}`}
                                title={value}
                                flex={column.flex}
                                badge={isShared && (
                                    <Tooltip title={`Shared with you by ${sharedByName}`} placement="top" arrow>
                                        <PeopleAltOutlinedIcon
                                            sx={{
                                                fontSize: '1rem',
                                                color: 'text.disabled',
                                                flexShrink: 0,
                                                '&:hover': { color: 'info.main' },
                                                transition: 'color 0.15s',
                                            }}
                                        />
                                    </Tooltip>
                                )}
                            />
                        );
                    }

                    // Render Author as a chip with avatar
                    if (column.field === 'userDisplayName') {
                        return (
                            <AuthorChip
                                key={index}
                                userId={report.userId}
                                userDisplayName={value}
                                flex={column.flex}
                                avatarProfile={report.authorProfiles?.[report.userId] ?? null}
                            />
                        );
                    }

                    // Related ICR as a clickable chip linking to the ICR page
                    if (column.field === 'icrSubject') {
                        return (
                            <Stack key={index} flex={column.flex} justifyContent="center" minWidth={0}>
                                {icrNumber ? (
                                    <Tooltip title={icrNumber} placement="top-start">
                                        <Chip
                                            label={icrSubject || icrNumber}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                            component="a"
                                            href={`/icr/${encodeURIComponent(icrNumber)}`}
                                            clickable
                                            onClick={(e) => e.stopPropagation()}
                                            sx={{
                                                width: 'fit-content',
                                                maxWidth: '100%',
                                                '& .MuiChip-label': {
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                },
                                            }}
                                        />
                                    </Tooltip>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">—</Typography>
                                )}
                            </Stack>
                        );
                    }

                    // Status badge (+ validation step icons when awaiting review)
                    if (column.field === 'status') {
                        const statusLabel = value || 'Unknown';
                        return (
                            <Stack
                                key={index}
                                flex={column.flex}
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                sx={{flexShrink: 0}}
                            >
                                <Chip
                                    label={statusLabel}
                                    size="small"
                                    color={getReportStatusChipColor(statusLabel)}
                                    variant="filled"
                                    sx={{flexShrink: 0, width: 'fit-content', textTransform: 'capitalize'}}
                                />
                                {statusLabel === 'Awaiting Review' && (
                                    <ValidationStatusCell
                                        validation={report.validation}
                                        productTypeValue={report.productType?.value || null}
                                    />
                                )}
                            </Stack>
                        );
                    }

                    // Disclosures — clickable count chip (FINTEL only); other rows show a dash
                    if (column.field === 'disclosureCount') {
                        if (!isFintel) {
                            return (
                                <Stack key={index} flex={column.flex} justifyContent="center">
                                    <Typography variant="body2" color="text.secondary">—</Typography>
                                </Stack>
                            );
                        }
                        const count = Number.isFinite(value) ? value : 0;
                        return (
                            <Stack key={index} flex={column.flex} justifyContent="center">
                                <Chip
                                    label={count > 0
                                        ? `${count} disclosure${count !== 1 ? 's' : ''}`
                                        : 'No disclosures'}
                                    size="small"
                                    color={count > 0 ? 'primary' : 'default'}
                                    variant="outlined"
                                    onClick={handleDisclosureCountClick}
                                    sx={{cursor: 'pointer', width: 'fit-content'}}
                                />
                            </Stack>
                        );
                    }

                    return (
                        <Typography key={index} flex={column.flex}>
                            {formatValue(value)}
                        </Typography>
                    );
                })}

                {/* Actions menu button */}
                <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ml: 'auto'}}>
                    <IconButton
                        data-menu-trigger
                        size="small"
                        aria-label="more"
                        aria-haspopup="true"
                        onClick={handleMenuOpen}
                    >
                        <MoreVertIcon fontSize="small"/>
                    </IconButton>
                </Stack>
            </Stack>

            <TableRowActionsMenu
                anchorEl={menuAnchorEl}
                open={menuOpen}
                onClose={() => setMenuAnchorEl(null)}
                role={role}
                onExport={handleExport}
                exporting={exporting}
                onShareOpen={() => setShareOpen(true)}
                onDeleteOpen={() => setConfirmOpen(true)}
                canDelete={canDelete}
                onCreateDisclosure={handleCreateDisclosure ? handleCreateDisclosureClick : null}
                onResetToDraft={handleResetToDraft ? handleResetToDraftClick : null}
                resettingToDraft={resettingToDraft}
            />

            <DeleteReportDialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={handleDeleteConfirm}
                deleting={deleting}
                title={report.title}
            />

            {role === 'owner' && (
                <ShareDialog
                    open={shareOpen}
                    onClose={() => setShareOpen(false)}
                    reportId={report.id}
                />
            )}

            {isFintel && (
                <DisclosuresListModal
                    open={disclosuresListOpen}
                    onClose={() => setDisclosuresListOpen(false)}
                    parentReportId={report.id}
                    parentTitle={report.title}
                    onDisclosureClick={handleDisclosureClick}
                />
            )}
        </>
    );
};

export default TableRow;
